/**
 * @copyright 2025 Cooltrax Pty Ltd. All rights reserved.
 */
import React from 'react'
import { createBrowserRouter, RouterProvider } from 'react-router-dom'
import { ErrorBoundary } from 'react-error-boundary'

// Removed Auth0Provider import – now handled by SDKProvider
import { Alert } from '@cooltrax/jellyfish-ui'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'

const ErrorPage = React.lazy(() => import('../pages/ErrorPage'))

import { createRoutes } from '../routes'
import './App.css'
import { SDKProvider } from '@cooltrax/jellyfish-sdk'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,
      gcTime: 5 * 60 * 1000,
      retry: 1,
    },
  },
})

const AppProviders: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { VITE_AUTH0_AUDIENCE, VITE_AUTH0_CLIENT_ID, VITE_AUTH0_DOMAIN, VITE_AUTH0_SCOPE, VITE_JELLYFISH_SERVER_URL } =
    import.meta.env

  if (!VITE_AUTH0_AUDIENCE || !VITE_AUTH0_CLIENT_ID || !VITE_AUTH0_DOMAIN || !VITE_JELLYFISH_SERVER_URL)
    return <Alert message="Error loading configuration" type="error" />

  return (
    <ErrorBoundary FallbackComponent={ErrorPage}>
      <SDKProvider
        config={{
          auth0: {
            domain: String(VITE_AUTH0_DOMAIN),
            clientId: String(VITE_AUTH0_CLIENT_ID),
            audience: String(VITE_AUTH0_AUDIENCE),
            scope: VITE_AUTH0_SCOPE ? String(VITE_AUTH0_SCOPE) : undefined,
            redirect_uri: `${window.location.origin}`,
          },
          api: { baseUrl: String(VITE_JELLYFISH_SERVER_URL) },
        }}
        onInitialized={() => {
          if (import.meta.env.DEV) console.log('SDKProvider initialized')
        }}
      >
        <script type="text/javascript" src="//script.crazyegg.com/pages/scripts/0118/1546.js" async></script>
        <QueryClientProvider client={queryClient}>
          {children}
          <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
      </SDKProvider>
    </ErrorBoundary>
  )
}

// Create the router with proper React Router v7 structure
const router = createBrowserRouter(createRoutes())

const WrappedApp: React.FC = () => (
  <AppProviders>
    <RouterProvider router={router} />
  </AppProviders>
)

export default WrappedApp
